import { type FragmentOf, readFragment } from "@opensea/graphql"
import { Button } from "@opensea/next-ui-kit"
import { Flex, SpaceBetween, TextBody } from "@opensea/ui-kit"
import { Apple, Google, Outlook, Yahoo } from "@opensea/ui-kit/icons"
import {
  GoogleCalendar,
  ICalendar,
  OutlookCalendar,
  YahooCalendar,
} from "datebook"
import { useTranslations } from "next-intl"
import { useShallow } from "zustand/react/shallow"
import { useDropStore } from "@/components/collection/drops/components/CollectionDropContext"
import { useCreateCalendarEvent } from "@/components/collection/drops/use-create-calendar-event"
import { AddToCalendarOptionsFragment } from "./AddToCalendarOptions.fragment"

type AddToCalendarOptionsProps = {
  drop: FragmentOf<typeof AddToCalendarOptionsFragment> | null
  selectedStageIndex: string
}

function CalendarOptionRow({
  icon,
  title,
  add,
}: {
  icon: React.ReactNode
  title: string
  add: () => void
}) {
  const t = useTranslations("SetDropReminderModal")
  return (
    <SpaceBetween>
      <Flex className="items-center gap-4">
        {icon}
        <TextBody size="sm" weight="semibold">
          {title}
        </TextBody>
      </Flex>
      <Button onClick={add} size="sm" variant="secondary">
        {t("add")}
      </Button>
    </SpaceBetween>
  )
}

export function AddtoCalendarOptions({
  drop: dropKey,
  selectedStageIndex,
}: AddToCalendarOptionsProps) {
  const [collectionName, collectionSlug] = useDropStore(
    useShallow(state => [state.collectionName, state.collectionSlug]),
  )
  const drop = readFragment(AddToCalendarOptionsFragment, dropKey)
  const selectedStage = drop?.stages.find(
    stage => stage.stageIndex === Number(selectedStageIndex),
  )
  const createCalendarEvent = useCreateCalendarEvent({
    collectionName,
    collectionSlug,
  })
  return (
    <>
      <CalendarOptionRow
        add={() => {
          if (!selectedStage) {
            return null
          }
          const event = createCalendarEvent(selectedStage)
          const calendar = new GoogleCalendar(event)
          window.open(calendar.render(), "_blank")
        }}
        icon={<Google />}
        title="Google Calendar"
      />
      <CalendarOptionRow
        add={() => {
          if (!selectedStage) {
            return null
          }
          const event = createCalendarEvent(selectedStage)
          const calendar = new ICalendar(event)
          const calendarContent = calendar.render()
          window.open(
            `data:text/calendar;charset=utf-8;base64,${btoa(calendarContent)}`,
            "_blank",
          )
        }}
        icon={<Apple />}
        title="Apple Calendar"
      />
      <CalendarOptionRow
        add={() => {
          if (!selectedStage) {
            return null
          }
          const event = createCalendarEvent(selectedStage)
          const calendar = new OutlookCalendar(event)
          window.open(calendar.render(), "_blank")
        }}
        icon={<Outlook />}
        title="Outlook"
      />
      <CalendarOptionRow
        add={() => {
          if (!selectedStage) {
            return null
          }
          const event = createCalendarEvent(selectedStage)
          const calendar = new YahooCalendar(event)
          window.open(calendar.render(), "_blank")
        }}
        icon={<Yahoo />}
        title="Yahoo calendar"
      />
    </>
  )
}
